import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { masterDataApi } from "@/services/masterDataApi";

export function useTherapyAreas() {
  return useQuery({ queryKey: ["master", "therapy-areas"], queryFn: masterDataApi.therapyAreas });
}

export function useProjectCodes() {
  return useQuery({ queryKey: ["master", "project-codes"], queryFn: masterDataApi.projectCodes });
}

export function useKantataProjects(search: string) {
  const limit = 50;
  return useQuery({
    queryKey: ["master", "kantata-projects", search.trim(), limit],
    queryFn: () => masterDataApi.kantataProjects(search, limit),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useEvidenceTypes() {
  return useQuery({ queryKey: ["master", "evidence-types"], queryFn: masterDataApi.evidenceTypes });
}

export function useSourceTypes() {
  return useQuery({ queryKey: ["master", "source-types"], queryFn: masterDataApi.sourceTypes });
}

// Admin list hooks (all records including inactive)
export function useAdminTherapyAreas() {
  return useQuery({ queryKey: ["admin", "therapy-areas"], queryFn: masterDataApi.adminTherapyAreas });
}

export function useAdminProjectCodes() {
  return useQuery({ queryKey: ["admin", "project-codes"], queryFn: masterDataApi.adminProjectCodes });
}

// Admin mutations — shared factory to avoid duplication
function useMasterMutations(
  queryKeys: string[][],
  createFn: (name: string) => Promise<unknown>,
  updateFn: (id: string, data: Record<string, unknown>) => Promise<unknown>,
  deleteFn: (id: string) => Promise<void>,
) {
  const qc = useQueryClient();
  const invalidate = () => queryKeys.forEach((queryKey) => qc.invalidateQueries({ queryKey }));

  const create = useMutation({ mutationFn: createFn, onSuccess: invalidate });
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => updateFn(id, data),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: deleteFn, onSuccess: invalidate });

  return { create, update, remove };
}

export function useTherapyAreaAdmin() {
  return useMasterMutations(
    [["master", "therapy-areas"], ["admin", "therapy-areas"]],
    masterDataApi.createTherapyArea,
    masterDataApi.updateTherapyArea,
    masterDataApi.deleteTherapyArea,
  );
}

export function useProjectCodeAdmin() {
  return useMasterMutations(
    [["master", "project-codes"], ["admin", "project-codes"]],
    masterDataApi.createProjectCode,
    masterDataApi.updateProjectCode,
    masterDataApi.deleteProjectCode,
  );
}

export function useEvidenceTypeAdmin() {
  return useMasterMutations(
    [["master", "evidence-types"]],
    masterDataApi.createEvidenceType,
    masterDataApi.updateEvidenceType,
    masterDataApi.deleteEvidenceType,
  );
}

export function useSourceTypeAdmin() {
  return useMasterMutations(
    [["master", "source-types"]],
    masterDataApi.createSourceType,
    masterDataApi.updateSourceType,
    masterDataApi.deleteSourceType,
  );
}

